import { Injectable, inject } from "@angular/core";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import { Trust } from "@models/estateProfile.model";
import { WebLogger } from "@weps/shared-http";

/**
 * OrphanValidationService
 *
 * Provides validation logic for detecting orphaned references in accounts and trusts.
 * An "orphaned" reference occurs when a person is deleted from Family & Relationships
 * but is still referenced as an account owner or trust settlor.
 *
 * This service ensures a single source of truth for orphan detection logic used by:
 * - accounts-table-v2 component (for warning icon display)
 * - owner strategy components (for edit-time validation)
 */
@Injectable({ providedIn: "root" })
export class OrphanValidationService {
  private readonly globalStore = inject(GlobalStateStore);

  /**
   * Checks if an ownerId references a person that no longer exists in the estate profile,
   * or if the owner is missing (NULL/undefined) for non-PRIMARY accounts.
   *
   * @param ownerId - The owner ID to check (uuid or piClientId)
   * @param ownerType - The owner type (e.g., 'PRIMARY', 'TRUST_OWNER', 'SECONDARY')
   * @returns true if the owner is orphaned (references a deleted person or is NULL)
   */
  isOrphanedOwner(ownerId: string | undefined, ownerType: string | undefined): boolean {
    const estateProfile = this.globalStore.estateProfile?.();
    const primaryClient = estateProfile?.primaryClient;
    const people = estateProfile?.people || [];

    // If ownerType is PRIMARY, trust that - not orphaned
    // (ownerId may be stale but ownerType is authoritative)
    if (ownerType === "PRIMARY") {
      return false;
    }

    // If ownerId is NULL/undefined for non-PRIMARY accounts, it's orphaned
    // (owner was removed via deleteSettlorOrOwner mutation)
    if (!ownerId) {
      return true;
    }

    // Check if ownerId matches primaryClient (uuid or piClientId) - not orphaned
    if (primaryClient?.uuid === ownerId || primaryClient?.piClientId === ownerId) {
      return false;
    }

    // Check if ownerId exists in people array (uuid or piClientId)
    const ownerExists = people.some((p) => p.uuid === ownerId || p.piClientId === ownerId);

    // If owner not found in people array (and not primary client), it's orphaned
    return !ownerExists;
  }

  /**
   * Checks if a trust has orphaned or missing settlors.
   * Returns true if:
   * 1. The trust has no settlors (empty array - settlors were removed)
   * 2. Any settlor reference points to a person that no longer exists
   *
   * @param trust - The trust to check
   * @returns true if the trust has orphaned settlors
   */
  hasOrphanedSettlor(trust: Trust | null): boolean {
    if (!trust) return false;

    // A trust with no settlors indicates settlors were removed - show warning
    if (!trust.settlors || trust.settlors.length === 0) return true;

    const estateProfile = this.globalStore.estateProfile?.();
    const primaryClient = estateProfile?.primaryClient;
    const people = estateProfile?.people || [];

    // Check if any settlor's householdMemberId is missing from people array and primary client
    return trust.settlors.some((settlor) => {
      if (!settlor.householdMemberId) return false;

      // Check if settlor matches primary client (uuid or piClientId)
      if (
        primaryClient?.uuid === settlor.householdMemberId ||
        primaryClient?.piClientId === settlor.householdMemberId
      ) {
        return false; // Settlor is primary client, not orphaned
      }

      // Check if settlor exists in people array (uuid or piClientId)
      const settlorExists = people.some(
        (p) => p.uuid === settlor.householdMemberId || p.piClientId === settlor.householdMemberId,
      );

      return !settlorExists; // Return true if settlor not found (orphaned)
    });
  }

  /**
   * Finds a trust by owner ID from a provided list of trusts.
   * Searches by ownerPoid (Vanguard trusts) and ownerId (External/manual trusts).
   *
   * @param ownerId - The owner ID to search for
   * @param trusts - Array of trusts to search in
   * @returns The matching trust or null
   */
  findTrustByOwnerId(ownerId: string | undefined, trusts: Trust[]): Trust | null {
    if (!ownerId) return null;

    // Try matching by referenceIds.clientPoid (Vanguard trusts)
    const trustByPoid = trusts.find((trust) => trust.referenceIds?.clientPoid === ownerId);
    if (trustByPoid) {
      return trustByPoid;
    }

    // Fall back to householdMemberId (External/manual trusts)
    const trustById = trusts.find((trust) => trust.householdMemberId === ownerId);
    if (trustById) {
      return trustById;
    }

    WebLogger.log({
      message: "OrphanValidationService: No trust found for ownerId",
      level: "warn",
      payload: { ownerId },
    });

    return null;
  }
}
